import { useEffect, useState } from 'react';
import Editor from '@monaco-editor/react';
import '../textEditor/monacoSetup';
import './markdownPreview.css';

function resolveMonacoTheme(): 'vs' | 'vs-dark' {
  if (typeof document === 'undefined') return 'vs';
  return document.documentElement.getAttribute('data-theme') === 'dark' ? 'vs-dark' : 'vs';
}

export type MarkdownSourcePaneProps = {
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
};

/** Raw Markdown source editor for split view (left pane). */
export function MarkdownSourcePane({ value, onChange, readOnly = false }: MarkdownSourcePaneProps) {
  const [theme, setTheme] = useState(resolveMonacoTheme);

  useEffect(() => {
    if (typeof document === 'undefined') return;
    const root = document.documentElement;
    const sync = () => setTheme(resolveMonacoTheme());
    const observer = new MutationObserver((mutations) => {
      if (mutations.some((m) => m.attributeName === 'data-theme')) sync();
    });
    observer.observe(root, { attributes: true, attributeFilter: ['data-theme'] });
    return () => observer.disconnect();
  }, []);

  return (
    <div className="markdown-source-pane" aria-label="Markdown source">
      <Editor
        key={theme}
        language="markdown"
        theme={theme}
        value={value}
        onChange={(next) => onChange(next ?? '')}
        loading={<div className="markdown-source-pane__loading">Loading editor…</div>}
        options={{
          readOnly,
          wordWrap: 'on',
          minimap: { enabled: false },
          fontSize: 13,
          lineNumbers: 'on',
          scrollBeyondLastLine: false,
          automaticLayout: true,
          renderWhitespace: 'selection',
          tabSize: 2,
          padding: { top: 12, bottom: 12 }
        }}
      />
    </div>
  );
}
